import { Button, Input, Select, Space, Typography } from 'antd';
import { useParams, useSearchParams } from 'react-router-dom';

import { CLOSE, PLUS } from '../icons';
import { CurrencyControl, InputCardLookup } from '../Input';
import DateField from '../common/DateField';
import UserPickerSelect from '../users/UserPickerSelect';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { isCurrencyValue } from '../../utils/currency';
import { LOOKUP_DATA_TYPE } from '../../utils/lookups';
import { USER_DATA_TYPE, userIdsOf } from '../../utils/userElements';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { choiceListOf } from '../../utils/choiceValues';

/**
 * "Required only when…": the conditional half of Required.
 *
 * Reason for variance, why late, damage notes, cancellation reason, other —
 * specify. All the same shape: a field that is noise on the ordinary row and
 * the whole point of the exceptional one. Asking always trains people to
 * type a dot; asking never loses the entry that mattered.
 *
 * Clauses are ANDed. An OR is two rules, and the one people mean is almost
 * always AND — offering both would make the common case carry the rare one's
 * vocabulary.
 *
 * THE "IS NOT" TRAP, which is why this panel warns. `result is not "No
 * defects"` looks like the rule an author means and is not: a row the
 * generator just wrote has NO result yet, and a blank IS "not equal to No
 * defects" (utils/required/required.js `clauseHolds`), so every unworked row
 * on the sheet demands a reason before it will save. `is none of` has the
 * same shape and the same hole. The rule that works names the values that
 * DO demand the field — `is one of` — because a blank is in no list. The API
 * enforces the clause correctly either way; this is where an author is saved
 * from writing the one they did not mean.
 */

const { Text } = Typography;

const OP_OPTIONS = [
	{ value: 'eq', label: 'is' },
	{ value: 'ne', label: 'is not' },
	{ value: 'in', label: 'is one of' },
	{ value: 'not-in', label: 'is none of' },
	{ value: 'empty', label: 'is empty' },
	{ value: 'not-empty', label: 'is not empty' },
	{ value: 'gt', label: 'is more than' },
	{ value: 'lt', label: 'is less than' },
];

const NO_VALUE = new Set([ 'empty', 'not-empty' ]);
const LIST_VALUE = new Set([ 'in', 'not-in' ]);
// The two that a BLANK satisfies. See the trap in this file's header.
const BLANK_SATISFIES = new Set([ 'ne', 'not-in' ]);

const labelOf = el => el?.label?.singular || el?.name || '';

const asList = value => (Array.isArray(value) ? value : []);

/**
 * The collection a lookup's items are picked from: the one that owns the card
 * being edited, or the one a new card is being made in (`?collectionId=`).
 * Empty when neither is known (a global card), where there are no items to
 * name anyway.
 */
function useCardCollectionId() {
	const { cardId } = useParams();
	// The router's address, not `window.location`: under a memory router (a
	// test, a preview) and across a navigation they are not the same.
	const [searchParams] = useSearchParams();
	const cache = useOptionalCardDefinitions();
	const card = cardId ? cache?.cardsById?.[ cardId ] : null;
	const owner = card?.collection_id ? String(card.collection_id?._id || card.collection_id) : '';
	if (owner) return owner;
	return searchParams.get('collectionId') || '';
}

/**
 * A list of values picked one at a time with the element's own input: the
 * entries already chosen, each with a remove button, and one empty input to
 * add the next. `render(value, commit, key, held)` draws the input.
 */
function PickedList({ values, render, onChange, disabled, label, same }) {
	return (
		<div className='required-when__list'>
			{values.map((entry, n) => (
				<div className='required-when__entry' key={n}>
					<div className='required-when__entry-value'>{render(entry, () => {}, `${ n }`, true)}</div>
					<Button
						type='text'
						icon={<CLOSE />}
						aria-label={`Remove ${ label } ${ n + 1 }`}
						disabled={disabled}
						onClick={() => onChange(values.filter((x, at) => at !== n))}
					/>
				</div>
			))}
			<div className='required-when__entry-value'>
				{render(null, (next) => {
					if (next === undefined || next === null || next === '') return;
					if (values.some(x => same(x, next))) return;
					onChange([ ...values, next ]);
				}, `new-${ values.length }`, false)}
			</div>
		</div>
	);
}

export default function RequiredWhenOptions({ element, elements = [], index, onChange, disabled = false }) {

	const collectionId = useCardCollectionId();

	const clauses = Array.isArray(element?.options?.requiredWhen) ? element.options.requiredWhen : [];
	// Never itself: a field that makes itself required can never be
	// satisfied, and the API refuses the pair at save.
	const others = (elements || []).filter(el => el?.name && el.name !== element?.name);
	const path = `elements[${ index }].options.requiredWhen`;

	// Unconditional wins: offering both would ask the reader which one is in
	// force, and the answer ("both, and `required` decides") is not worth
	// teaching.
	if (element?.options?.required) {
		return clauses.length ? (
			<Text type='secondary' className='d-block'>
				This element is always required, so its conditions are not in force.
			</Text>
		) : null;
	}

	if (!others.length) return null;

	const emit = next => onChange(path, next.length ? next : undefined);
	const patch = (i, changes) => emit(clauses.map((clause, index2) => (index2 === i ? { ...clause, ...changes } : clause)));

	/**
	 * The same intent written so a blank does not satisfy it: the choices the
	 * clause EXCLUDES become the list that requires the field. Only possible
	 * for a choice-backed source — a free-text element has no set to
	 * complement, so there the author gets the warning and no button.
	 */
	const invert = (clause) => {
		const choices = optionsFor(clause);
		if (!choices) return null;
		const excluded = new Set((clause.op === 'not-in'
			? (Array.isArray(clause.value) ? clause.value : [])
			: [ clause.value ]).map(v => String(v)));
		const kept = choices.map(c => c.value).filter(v => !excluded.has(String(v)));
		// Every choice excluded leaves a clause that can never hold, which is
		// not what the author meant either — better to leave their words alone.
		return kept.length ? { op: 'in', value: kept } : null;
	};

	const optionsFor = (clause) => {
		const source = others.find(el => el.name === clause.element);
		// A Choice's list only (KPE-033): another type's list is inert.
		const choices = choiceListOf(source);
		if (!choices.length) return null;
		return choices.map(choice => (typeof choice === 'string'
			? { value: choice, label: choice }
			: { value: choice.value, label: choice.label || choice.value }));
	};

	const sourceOf = clause => others.find(el => el.name === clause.element);

	// Each clause's value is entered with the input the element itself uses, so
	// nothing is typed in a stored text shape (comma-separated, or an amount
	// followed by its currency code). The stored values keep their shapes: money
	// is `{ amount, currency }` (utils/required/required.js), a date its
	// 'YYYY-MM-DD', a user or an item its id.
	const renderValue = (clause, i) => {
		const source = sourceOf(clause);
		const list = LIST_VALUE.has(clause.op);
		const label = `Condition ${ i + 1 } value`;
		const set = value => patch(i, { value });
		const type = source?.dataType;

		if (type === 'currency') {
			const control = (value, commit, key, held) => (
				<CurrencyControl
					key={key}
					id={held ? undefined : `${ path }[${ i }].value`}
					size='middle'
					value={isCurrencyValue(value) ? value : null}
					element={source}
					disabled={disabled}
					aria-label={held ? `${ label } ${ Number(key) + 1 }` : (list ? `${ label }, add an amount` : label)}
					onChange={next => commit(isCurrencyValue(next) ? next : undefined)}
				/>
			);
			if (list) {
				return <PickedList values={asList(clause.value)} label='amount' disabled={disabled}
					same={(a, b) => a?.amount === b?.amount && a?.currency === b?.currency}
					render={control} onChange={set} />;
			}
			return <div className='required-when__value'>{control(clause.value, next => set(next), 'one', false)}</div>;
		}

		if (type === 'date' || type === 'date-time') {
			const field = (value, commit, key, held) => (
				<DateField
					key={key}
					mode={type}
					elementName={`${ path }[${ i }].value`}
					elementLabel={held ? `${ label } ${ Number(key) + 1 }` : (list ? `${ label }, add a date` : label)}
					value={value || null}
					disabled={disabled}
					onChange={(name, next) => commit(next || undefined)}
				/>
			);
			if (list) {
				return <PickedList values={asList(clause.value)} label='date' disabled={disabled}
					same={(a, b) => a === b} render={field} onChange={set} />;
			}
			return <div className='required-when__value'>{field(typeof clause.value === 'string' ? clause.value : null, next => set(next), 'one', false)}</div>;
		}

		if (type === USER_DATA_TYPE) {
			return (
				<div className='required-when__value'>
					<UserPickerSelect
						collectionId={collectionId || undefined}
						multiple={list}
						value={list ? userIdsOf(clause.value) : (userIdsOf(clause.value)[ 0 ] || undefined)}
						onChange={next => set(list ? userIdsOf(next) : (userIdsOf(next)[ 0 ] || undefined))}
						disabled={disabled}
						placeholder={list ? 'Pick people' : 'Pick a person'}
						aria-label={label}
					/>
				</div>
			);
		}

		if (type === LOOKUP_DATA_TYPE && source?.options?.lookupCardId && collectionId) {
			return (
				<div className='required-when__value'>
					<InputCardLookup
						elementName={`${ path }[${ i }].value`}
						elementLabel={label}
						collectionId={collectionId}
						elementOptions={{ ...source.options, allowMultiple: list }}
						value={list ? asList(clause.value) : (clause.value || null)}
						onChange={(name, next) => set(list ? asList(next) : (next || undefined))}
						disabled={disabled}
						showMe={false}
					/>
				</div>
			);
		}

		const choiceOptions = optionsFor(clause);
		if (choiceOptions) {
			return (
				<Select
					style={{ minWidth: 180 }}
					mode={list ? 'multiple' : undefined}
					value={clause.value}
					placeholder='Value'
					aria-label={label}
					disabled={disabled}
					options={choiceOptions}
					onChange={set}
				/>
			);
		}

		// Any other element: a list is pills (type a value, press Enter), a
		// single value is one box.
		if (list) {
			return (
				<Select
					style={{ minWidth: 220 }}
					mode='tags'
					tokenSeparators={[]}
					value={asList(clause.value).map(String)}
					placeholder='Type a value, then press Enter'
					aria-label={label}
					disabled={disabled}
					onChange={next => set(next.map(v => String(v).trim()).filter(Boolean))}
				/>
			);
		}
		return (
			<Input
				{...NO_TEXT_ASSIST}
				style={{ minWidth: 180 }}
				value={clause.value ?? ''}
				placeholder='Value'
				aria-label={label}
				disabled={disabled}
				onChange={event => set(event.target.value)}
			/>
		);
	};

	return (
		<div className='card-def-element__required-when'>
			<Text strong className='d-block'>Required only when</Text>
			{clauses.map((clause, i) => {
				return (
					<div key={i}>
					<Space align='start' wrap style={{ display: 'flex', marginBottom: 8 }}>
						<Select
							style={{ minWidth: 160 }}
							value={clause.element || undefined}
							placeholder='Which element'
							aria-label={`Condition ${ i + 1 } element`}
							disabled={disabled}
							options={others.map(el => ({ value: el.name, label: labelOf(el) }))}
							onChange={value => patch(i, { element: value, value: undefined })}
						/>
						<Select
							style={{ width: 150 }}
							value={clause.op || 'eq'}
							aria-label={`Condition ${ i + 1 } test`}
							disabled={disabled}
							options={OP_OPTIONS}
							onChange={value => patch(i, { op: value, value: LIST_VALUE.has(value) ? [] : undefined })}
						/>
						{NO_VALUE.has(clause.op) ? null : renderValue(clause, i)}
						<Button
							type='text'
							danger
							icon={<CLOSE />}
							aria-label={`Remove condition ${ i + 1 }`}
							disabled={disabled}
							onClick={() => emit(clauses.filter((c, index2) => index2 !== i))}
						/>
					</Space>
					{BLANK_SATISFIES.has(clause.op) ? (
						<Text type='warning' className='d-block' style={{ marginTop: -4, marginBottom: 8 }}>
							A row that has no {labelOf(others.find(el => el.name === clause.element)) || 'value'} yet
							{' '}satisfies this, so every untouched row will demand
							{' '}{labelOf(element) || 'this field'}. Name the values that
							{' '}<em>do</em> demand it with “is one of” instead.
							{invert(clause) ? (
								<Button
									type='link'
									size='small'
									style={{ paddingLeft: 4 }}
									disabled={disabled}
									onClick={() => patch(i, invert(clause))}
								>
									Switch to “is one of”
								</Button>
							) : null}
						</Text>
					) : null}
					</div>
				);
			})}
			{clauses.length < 5 ? (
				<Button
					size='small'
					type='dashed'
					icon={<PLUS />}
					disabled={disabled}
					onClick={() => emit([ ...clauses, { element: others[ 0 ].name, op: 'eq', value: '' } ])}
				>
					{clauses.length ? 'Add another condition' : 'Add a condition'}
				</Button>
			) : null}
			{clauses.length > 1 ? (
				<Text type='secondary' className='d-block'>All of these have to be true.</Text>
			) : null}
		</div>
	);
}
